import PouchDB from "pouchdb";

/** Minimal interface for documents that can be stored in PouchDB */
interface PouchDocument {
  _id: string;
  _rev?: string;
  _deleted?: boolean;
}

export interface SyncOptions {
  remoteUrl: string;
  dbName: string;
  getToken: () => Promise<string | null>;
  live?: boolean;
}

export class DatabaseService {
  private db: PouchDB.Database;

  constructor(dbName: string) {
    this.db = new PouchDB(dbName);
  }

  async allDocs(): Promise<unknown[]> {
    const result = await this.db.allDocs({ include_docs: true });
    return result.rows
      .filter(row => row.doc && !row.id.startsWith("_design/"))
      .map(row => row.doc);
  }

  async put<T extends PouchDocument>(doc: T): Promise<T> {
    const { rev } = await this.db.put(doc);
    return { ...doc, _rev: rev };
  }

  async remove<T extends PouchDocument>(doc: T): Promise<T> {
    const { rev } = await this.db.put({ ...doc, _deleted: true });
    return { ...doc, _rev: rev };
  }

  async destroy() {
    await this.db.destroy();
  }

  onChange(callback: (change: any) => void) {
    return this.db
      .changes({
        since: "now",
        live: true,
        include_docs: true,
      })
      .on("change", callback);
  }

  syncWith(remoteDb: PouchDB.Database, live: boolean = true) {
    return this.db.sync(remoteDb, { live, retry: live });
  }

  setupSync(options: SyncOptions) {
    const { remoteUrl, dbName, getToken, live = true } = options;
    const remoteDbUrl = `${remoteUrl}/${dbName}`;

    const remoteDb = new PouchDB(remoteDbUrl, {
      skip_setup: true,
      fetch: async (url, opts) => {
        const token = await getToken();
        const headers = new Headers(opts?.headers);
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return fetch(url, { ...opts, headers });
      },
    });

    return this.syncWith(remoteDb, live);
  }
}

export function createDatabase(dbName: string): DatabaseService {
  return new DatabaseService(dbName);
}
